import { useRef, useState } from 'react';
import { VoucherPMGoogleAuth, SettingGoogleAuthType } from '../../voucher-perfectmoney/google-auth/google-auth';

/**
 * Small helper that wires the reusable Google Authenticator (2FA) modal for any
 * settings save that requires a `googleAuthCode`.
 *
 * Usage:
 *   const { requestAuth, modal } = useGoogleAuth();
 *   ...
 *   requestAuth(async (code) => {
 *       const res = await postViaAuth(url, { ...body, googleAuthCode: code });
 *       return responseValidator(res.status); // true => close modal, false => keep open with error
 *   });
 *   ...
 *   return (<>{modal}</>);
 */
export const useGoogleAuth = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [data, setData] = useState<SettingGoogleAuthType>();
    const [loading, setLoading] = useState<boolean>(false);
    const submitRef = useRef<(code: string) => Promise<boolean>>();

    const requestAuth = (onSubmit: (code: string) => Promise<boolean>) => {
        setData(undefined);
        submitRef.current = onSubmit;
        setIsOpen(true);
    };

    const modal = (
        <VoucherPMGoogleAuth
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            data={data}
            setData={setData}
            loading={loading}
            submitData={async () => {
                if (!data?.code || loading) return;
                setLoading(true);
                try {
                    const ok = await submitRef.current?.(data.code);
                    if (ok) {
                        setIsOpen(false);
                    } else {
                        setData((prev) => ({ ...prev, error: 'خطا در تایید کد امنیتی. دوباره تلاش کنید.' }));
                    }
                } catch {
                    setData((prev) => ({ ...prev, error: 'ارتباط با سرور برقرار نشد.' }));
                } finally {
                    setLoading(false);
                }
            }}
        />
    );

    return { requestAuth, modal };
};
